import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import {
  Variables,
  useBulkCreatePolicyAssignments,
} from 'modules/timeoff/hooks/useBulkCreatePolicyAssignments'
import { useCurrentEmployeesInPolicy } from 'modules/timeoff/hooks/useCurrentEmployeesInPolicy'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type TimeoffPolicy from 'modules/timeoff/store/models/TimeoffPolicy/Policy'

interface Props {
  timeoffPolicyId: number
  onClose: () => void
  contractVersions: ContractVersions
  loading: boolean
}

const AddEmployeeAssignment = ({ timeoffPolicyId, onClose, contractVersions, loading }: Props) => {
  const [selectedEmployeeIds, setEmployeesIds] = React.useState<number[]>([])

  const getPolicy = (): TimeoffPolicy => {
    const timeoffPolicy = timeoffPolicies.get(timeoffPolicyId)

    if (!timeoffPolicy) {
      throw Error(`Error getting timeoffPolicies with id = ${timeoffPolicyId}`)
    }

    return timeoffPolicy
  }

  const policy = getPolicy()

  const { loading: loadingCurrentEmployees, currentPolicyAssignments } =
    useCurrentEmployeesInPolicy(policy.attributes.id)
  const currenltyAssignedEmployeeIds = currentPolicyAssignments.map((a) => a.employeeId)
  const availableEmployees = employees.filter((e) => !currenltyAssignedEmployeeIds.includes(e.id))

  const { loading: loadingBulkCreate, create: bulkCreate } = useBulkCreatePolicyAssignments({
    onCompleted(data) {
      if (data.timeoffMutations.bulkCreateTimeoffCurrentPolicyAssignment?.errors.length) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.timeoff_policies.add_employees_modal.assign.error'),
          time: 2000,
        })
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 2000,
        })
      }
    },
  })

  const bulkCreateAndClosePortal = (data: Variables, closePortal: ClosePortalFn) => {
    bulkCreate(data).then(() => {
      closePortal()
      onClose()
    })
  }

  const addEmployees = async (closePortal: ClosePortalFn) =>
    bulkCreateAndClosePortal(
      {
        employeeIds: selectedEmployeeIds,
        timeoffPolicyId: policy.attributes.id,
      },
      closePortal
    )

  const onSelectEmployee = (employeeId: number | null | undefined) =>
    employeeId ? setEmployeesIds(xor(selectedEmployeeIds, [employeeId])) : setEmployeesIds([])

  return (
    <Modal
      illustration="alert"
      title={i18n.t('settings.timeoff_policies.add_employees_modal.assign.title')}
      description={i18n.t('settings.timeoff_policies.add_employees_modal.assign.description')}
      onClose={onClose}
      onSubmit={addEmployees}
      footer={
        <Button disabled={!selectedEmployeeIds.length} submit>
          {i18n.t('settings.timeoff_policies.add_employees_modal.button')}
        </Button>
      }
    >
      {loading || loadingBulkCreate || loadingCurrentEmployees ? (
        <Loading />
      ) : availableEmployees.length === 0 && employees.active.length > 0 ? (
        <Box textAlign="center">
          <Text.Normal color="grey800">
            {i18n.t('settings.timeoff_policies.add_employees_modal.description')}
          </Text.Normal>
        </Box>
      ) : (
        <EmployeeSelector
          employees={availableEmployees}
          onSelectEmployee={onSelectEmployee}
          onSelectMultiple={setEmployeesIds}
          selectedEmployeeIds={selectedEmployeeIds}
          contractVersions={contractVersions}
        />
      )}
    </Modal>
  )
}

export default observer(AddEmployeeAssignment)
